import { IAPIAdapter } from 'plugins/tag/plugin_data_service/ts/Application/adapters/abstract/IAPIAdapter';
import { PlutoAdapter } from 'plugins/tag/plugin_data_service/ts/Application/adapters/pluto/PlutoAdapter';
import { PlutoLegacyAdapter } from 'plugins/tag/plugin_data_service/ts/Application/adapters/pluto_legacy/PlutoLegacyAdapter';
import { AdapterId } from 'plugins/tag/plugin_data_service/ts/Application/model/enums/AdapterId';
import { IParamsVO } from 'tag/ts/tag/interfaces/params/IParamsVO';
import { IAPIService } from './IAPIService';
import { IAPIServiceArgument } from './IAPIServiceArgument';

/**
 * @copyright © Paramount Streaming
 * @description APIService owns API adapters and delegates all API requests to the active adapter.
 * Only used by the Data Service plugin (TagDataService), not exposed to external clients.
 * Instantiates/selects the appropriate adapter when the service is created (tag start).
 */
export class APIService implements IAPIService {
	/**
	 * @description Adapter ID used when no explicit adapterId or partnerId is provided.
	 */
	static readonly #DEFAULT_ADAPTER_ID = AdapterId.PLUTO;

	/**
	 * @description Maps known partner IDs to their default adapter ID.
	 */
	static readonly #PARTNER_ADAPTER_MAP: Record<string, string> = {
		plutotv: AdapterId.PLUTO
	};

	/**
	 * @description All registered adapter instances. Adapter resolution searches this list by id.
	 */
	static readonly #ADAPTERS: IAPIAdapter[] = [
		new PlutoAdapter(),
		new PlutoLegacyAdapter()
	];

	/**
	 * @description The adapter selected for this instance's lifetime.
	 */
	#activeAdapter: IAPIAdapter | null = null;

	constructor( config: IAPIServiceArgument ) {
		this.#activeAdapter = this.#resolveAdapter( config?.api?.adapterId, config?.partnerId );
	}

	/**
	 * @description Delegates the fetch request to the active adapter.
	 * @param {IParamsVO} paramsVO
	 * @returns {Promise<unknown>} Parsed API response from the active adapter
	 */
	async fetchData( paramsVO: IParamsVO ): Promise<unknown> {
		const adapter = this.#assertActiveAdapter();
		return adapter.fetchData( paramsVO );
	}

	getActiveAdapter(): IAPIAdapter | null {
		return this.#activeAdapter;
	}

	/**
	 * @description Returns the ID of the currently active adapter.
	 * @returns {string}
	 */
	getAdapterId(): string {
		return this.#assertActiveAdapter().id;
	}

	/**
	 * @description Resolves the active adapter using a priority cascade:
	 * 1. Explicit adapterId from config
	 * 2. Mapped from partnerId
	 * 3. Default ('pluto')
	 * @param {string} [adapterId]
	 * @param {string} [partnerId]
	 * @returns {IAPIAdapter | null}
	 */
	#resolveAdapter( adapterId?: string, partnerId?: string ): IAPIAdapter | null {
		const adapters = APIService.#ADAPTERS;
		if ( !adapters.length ) {
			return null;
		}
		const resolvedId =
			adapterId
			?? ( partnerId ? APIService.#PARTNER_ADAPTER_MAP[ partnerId ] : undefined )
			?? APIService.#DEFAULT_ADAPTER_ID;

		return adapters.find( ( a ) => a.id === resolvedId ) ?? adapters[ 0 ] ?? null;
	}

	/**
	 * @description Asserts that an active adapter is configured, throwing if not.
	 * @returns {IAPIAdapter}
	 */
	#assertActiveAdapter(): IAPIAdapter {
		if ( !this.#activeAdapter ) {
			throw new Error( 'APIService: no active adapter configured.' );
		}
		return this.#activeAdapter;
	}
}